import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
const DAY_MS = 24 * 60 * 60 * 1000;
const INDEX_KEY = 'img_cache_index_v1';
const inMemoryUrlMap = new Map(); // src -> cached URL (blob or file URL)
const inflight = new Map();
function hashUrl(u) {
    // Simple DJB2-like hash
    let h = 5381;
    for (let i = 0; i < u.length; i++)
        h = ((h << 5) + h) + u.charCodeAt(i);
    return (h >>> 0).toString(16);
}
function loadIndex() {
    try {
        const raw = localStorage.getItem(INDEX_KEY);
        if (!raw)
            return {};
        return JSON.parse(raw);
    }
    catch {
        return {};
    }
}
function saveIndex(idx) {
    try {
        localStorage.setItem(INDEX_KEY, JSON.stringify(idx));
    }
    catch { }
}
function isNative() {
    try {
        return !!Capacitor?.platform;
    }
    catch {
        return false;
    }
}
function getExtFromUrl(url) {
    try {
        const u = new URL(url);
        const m = /\.([a-zA-Z0-9]+)$/.exec(u.pathname || '');
        if (m)
            return m[1].toLowerCase();
    }
    catch { }
    return 'img';
}
async function ensureCacheDir() {
    // noop: Filesystem will create intermediate dirs on writeFile
}
export async function getCachedImage(src) {
    if (!src)
        return undefined;
    const url = String(src);
    // Skip data URLs or bundled assets
    if (url.startsWith('data:') || url.startsWith('asset:') || url.startsWith('capacitor:'))
        return url;
    if (/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(url) === false && url.includes('assets/'))
        return url;
    // In-memory short-circuit
    const inMem = inMemoryUrlMap.get(url);
    if (inMem)
        return inMem;
    const inflightTask = inflight.get(url);
    if (inflightTask)
        return inflightTask;
    const p = (async () => {
        if (isNative()) {
            // Use Capacitor Filesystem for persistent cache
            const key = hashUrl(url);
            const ext = getExtFromUrl(url);
            const filePath = `images/${key}.${ext}`;
            let idx = loadIndex();
            const now = Date.now();
            const entry = idx[key];
            try {
                if (entry && now - entry.ts < DAY_MS) {
                    // verify file exists
                    try {
                        await Filesystem.stat({ path: entry.path, directory: Directory.Cache });
                        const fileUrl = Capacitor.convertFileSrc((await Filesystem.getUri({ path: entry.path, directory: Directory.Cache })).uri);
                        inMemoryUrlMap.set(url, fileUrl);
                        return fileUrl;
                    }
                    catch { }
                }
            }
            catch { }
            // download
            await ensureCacheDir();
            let arrayBuf;
            try {
                const res = await CapacitorHttp.request({ url, method: 'GET', responseType: 'arraybuffer' });
                // CapacitorHttp v5 returns data directly for arraybuffer
                arrayBuf = res.data;
            }
            catch {
                // fallback to fetch (may fail for CORS)
                const r = await fetch(url);
                arrayBuf = await r.arrayBuffer();
            }
            const base64 = arrayBufferToBase64(arrayBuf);
            await Filesystem.writeFile({ path: filePath, data: base64, directory: Directory.Cache });
            const fileUrl = Capacitor.convertFileSrc((await Filesystem.getUri({ path: filePath, directory: Directory.Cache })).uri);
            idx[key] = { url, path: filePath, ts: now };
            saveIndex(idx);
            inMemoryUrlMap.set(url, fileUrl);
            return fileUrl;
        }
        // Web/Electron: use Cache API if available, else in-memory blob URL
        try {
            const key = hashUrl(url);
            const lsKey = `img_ts_${key}`;
            const ts = Number(localStorage.getItem(lsKey) || '0');
            const now = Date.now();
            const cacheName = 'image-cache-v1';
            if ('caches' in window) {
                const c = await caches.open(cacheName);
                if (ts && now - ts < DAY_MS) {
                    const match = await c.match(url);
                    if (match) {
                        const blob = await match.blob();
                        const bUrl = URL.createObjectURL(blob);
                        inMemoryUrlMap.set(url, bUrl);
                        return bUrl;
                    }
                }
                const resp = await fetch(url, { cache: 'force-cache' });
                if (resp.ok) {
                    await c.put(url, resp.clone());
                    localStorage.setItem(lsKey, String(now));
                    const blob = await resp.blob();
                    const bUrl = URL.createObjectURL(blob);
                    inMemoryUrlMap.set(url, bUrl);
                    return bUrl;
                }
            }
        }
        catch { }
        // Fallback: return original URL; browser cache may still help
        inMemoryUrlMap.set(url, url);
        return url;
    })();
    inflight.set(url, p);
    try {
        return await p;
    }
    finally {
        inflight.delete(url);
    }
}
function arrayBufferToBase64(buf) {
    let binary = '';
    const bytes = new Uint8Array(buf);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++)
        binary += String.fromCharCode(bytes[i]);
    // btoa available in WebView
    return btoa(binary);
}
